import type {
  MatchPushPlan,
  PlanCopyOverride,
  PushLanguage,
  UpdateMatchPushPlanBody,
} from '@/types/matchPushes';
import { minuteOf } from '@/utils/cricketFixtureDiff';
import { LANGUAGES, LANGUAGE_LABEL, copyProblems, customCopyMode } from '@/utils/matchPushCopy';

/** The text boxes of the edit modal: every language, blank until filled. */
export type CopyDraft = Record<PushLanguage, { title: string; body: string }>;

/**
 * The modal's starting text from a plan's stored override. The override may
 * hold only some languages (or odd values, being JSON): the rest start blank.
 */
export function copyDraftFrom(override: PlanCopyOverride | null | undefined): CopyDraft {
  const text = (value: unknown): string => (typeof value === 'string' ? value : '');
  const draft = {} as CopyDraft;
  for (const language of LANGUAGES) {
    const stored = override?.[language];
    draft[language] = { title: text(stored?.title), body: text(stored?.body) };
  }
  return draft;
}

/**
 * The override to send and what is wrong with the draft. Only `languages` are
 * looked at, and only those the admin filled in: a language left empty stays
 * out, so it keeps using the Copy tab text. A language with just a title or
 * just a body is a problem, and so is having no language at all.
 */
export function copyOverrideFrom(
  draft: CopyDraft,
  languages: PushLanguage[]
): { override: PlanCopyOverride; problems: string[] } {
  const override: PlanCopyOverride = {};
  const problems: string[] = [];
  for (const language of languages) {
    const title = draft[language].title.trim();
    const body = draft[language].body.trim();
    if (!title && !body) continue;
    if (!title || !body) {
      problems.push(
        `${LANGUAGE_LABEL[language]}: give both a title and a body, or leave both empty`
      );
      continue;
    }
    problems.push(
      ...copyProblems(title, body, language).map((p) => `${LANGUAGE_LABEL[language]}: ${p}`)
    );
    override[language] = { title, body };
  }
  if (Object.keys(override).length === 0 && problems.length === 0) {
    problems.push('Write the copy in at least one language');
  }
  return { override, problems };
}

const sameOverride = (a: PlanCopyOverride | null, b: PlanCopyOverride): boolean => {
  if (!a) return false;
  const stored = LANGUAGES.filter((l) => a[l]);
  const sent = LANGUAGES.filter((l) => b[l]);
  return (
    stored.length === sent.length &&
    sent.every((l) => a[l]?.title === b[l]?.title && a[l]?.body === b[l]?.body)
  );
};

interface EditInput {
  /** The plan may still be moved (fixed, not sent). */
  canReschedule: boolean;
  /** The chosen send time as an ISO instant, or null when none is shown. */
  sendAtIso: string | null;
  /** Editable plans: custom copy is on. Clear-only plans: the override is kept. */
  custom: boolean;
  /** The languages the admin filled (see copyOverrideFrom). */
  copy: PlanCopyOverride;
}

/**
 * The PATCH body for the edit modal: only what changed. A result plan never
 * gets custom text (its copy depends on win or loss); an existing override on
 * one can only be cleared. The send time is compared by the minute, as the
 * picker has no seconds.
 */
export function planUpdateBody(plan: MatchPushPlan, input: EditInput): UpdateMatchPushPlanBody {
  const body: UpdateMatchPushPlanBody = {};
  if (input.canReschedule && input.sendAtIso) {
    const changed = !plan.scheduledAt || minuteOf(input.sendAtIso) !== minuteOf(plan.scheduledAt);
    if (changed) body.scheduledAt = input.sendAtIso;
  }
  const mode = customCopyMode(plan);
  if (mode === 'editable') {
    if (input.custom) {
      if (!sameOverride(plan.copyOverride, input.copy)) body.copyOverride = input.copy;
    } else if (plan.copyOverride) {
      body.copyOverride = null;
    }
  } else if (mode === 'clear-only' && !input.custom) {
    body.copyOverride = null;
  }
  return body;
}
